import { useState, useEffect } from "react";
import { Trash2, AlertCircle, RefreshCw } from "lucide-react";
import { supabase } from "../../../lib/supabase";

// API base URL from environment
const API_BASE_URL = import.meta.env.VITE_API_BASE_URL;

interface ApiToken {
  id: string;
  createdAt: string;
  expiresAt: string;
  isRevoked: boolean;
  revokedAt: string | null;
  lastUsedAt: string | null;
  description: string | null;
}

export function ApiTokenList() {
  const [tokens, setTokens] = useState<ApiToken[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [revoking, setRevoking] = useState<string | null>(null);

  // Function to fetch the user's API tokens
  const fetchTokens = async () => {
    try {
      setLoading(true);
      setError(null);

      // Get the current user's session for the auth token
      const {
        data: { session },
        error: sessionError,
      } = await supabase.auth.getSession();

      if (sessionError) {
        throw new Error("Failed to get session information");
      }

      if (!session) {
        throw new Error("You must be logged in to view API tokens");
      }

      // Fetch the user's tokens using the backend API
      const response = await fetch(`${API_BASE_URL}/v1/api-tokens`, {
        method: "GET",
        headers: {
          Authorization: `Bearer ${session.access_token}`,
        },
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(
          errorData.message || `Failed to fetch tokens: ${response.statusText}`
        );
      }

      const data = await response.json();
      setTokens(data || []);
    } catch (err) {
      console.error("Error fetching tokens:", err);
      setError(
        err instanceof Error ? err.message : "An unknown error occurred"
      );
    } finally {
      setLoading(false);
    }
  };

  // Function to revoke an API token
  const revokeToken = async (tokenId: string) => {
    try {
      setRevoking(tokenId);
      setError(null);

      // Get the current user's session for the auth token
      const {
        data: { session },
        error: sessionError,
      } = await supabase.auth.getSession();

      if (sessionError) {
        throw new Error("Failed to get session information");
      }

      if (!session) {
        throw new Error("You must be logged in to revoke API tokens");
      }

      // Delete the token using the backend API
      const response = await fetch(`${API_BASE_URL}/v1/api-tokens/${tokenId}`, {
        method: "DELETE",
        headers: {
          Authorization: `Bearer ${session.access_token}`,
        },
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(
          errorData.message || `Failed to revoke token: ${response.statusText}`
        );
      }

      // Refresh the token list
      fetchTokens();
    } catch (err) {
      console.error("Error revoking token:", err);
      setError(
        err instanceof Error ? err.message : "An unknown error occurred"
      );
    } finally {
      setRevoking(null);
    }
  };

  // Fetch tokens when the component mounts
  useEffect(() => {
    fetchTokens();
  }, []);

  // Format date for display
  const formatDate = (dateString: string | null) => {
    if (!dateString) return "N/A";
    return new Date(dateString).toLocaleString();
  };

  // Calculate token status
  const getTokenStatus = (token: ApiToken) => {
    if (token.isRevoked) {
      return {
        label: "Revoked",
        color: "text-red-600 bg-red-50",
      };
    }

    const now = new Date();
    const expiryDate = new Date(token.expiresAt);

    if (expiryDate < now) {
      return {
        label: "Expired",
        color: "text-gray-600 bg-gray-50",
      };
    }

    // Check if expiring soon (within 7 days)
    const sevenDaysFromNow = new Date();
    sevenDaysFromNow.setDate(sevenDaysFromNow.getDate() + 7);

    if (expiryDate < sevenDaysFromNow) {
      return {
        label: "Expiring Soon",
        color: "text-amber-600 bg-amber-50",
      };
    }

    return {
      label: "Active",
      color: "text-green-600 bg-green-50",
    };
  };

  return (
    <div className="mt-8">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-medium text-gray-900">Your API Tokens</h3>
        <button
          onClick={fetchTokens}
          disabled={loading}
          className="flex items-center px-3 py-1 text-sm font-medium text-indigo-600 border border-indigo-300 rounded-md hover:bg-indigo-50"
        >
          <RefreshCw
            className={`h-4 w-4 mr-1 ${loading ? "animate-spin" : ""}`}
          />
          {loading ? "Refreshing..." : "Refresh"}
        </button>
      </div>

      {error && (
        <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-md">
          <div className="flex">
            <AlertCircle className="h-5 w-5 text-red-500 mr-2 flex-shrink-0" />
            <p className="text-sm text-red-700">{error}</p>
          </div>
        </div>
      )}

      {loading && tokens.length === 0 ? (
        <div className="text-center py-8">
          <RefreshCw className="h-8 w-8 text-indigo-500 animate-spin mx-auto" />
          <p className="mt-2 text-gray-600">Loading tokens...</p>
        </div>
      ) : tokens.length === 0 ? (
        <div className="text-center py-8 border border-dashed border-gray-300 rounded-lg">
          <p className="text-gray-500">
            You haven't created any API tokens yet. Generate a token above to
            get started.
          </p>
        </div>
      ) : (
        <div className="bg-white shadow-sm border border-gray-200 rounded-lg overflow-hidden">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th
                  scope="col"
                  className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                >
                  Created
                </th>
                <th
                  scope="col"
                  className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                >
                  Expires
                </th>
                <th
                  scope="col"
                  className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                >
                  Last Used
                </th>
                <th
                  scope="col"
                  className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                >
                  Status
                </th>
                <th
                  scope="col"
                  className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider"
                >
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {tokens.map((token) => {
                const status = getTokenStatus(token);
                return (
                  <tr key={token.id}>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {formatDate(token.createdAt)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {formatDate(token.expiresAt)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {token.lastUsedAt
                        ? formatDate(token.lastUsedAt)
                        : "Never used"}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span
                        className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${status.color}`}
                      >
                        {status.label}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                      {!token.isRevoked && (
                        <button
                          onClick={() => revokeToken(token.id)}
                          disabled={revoking === token.id}
                          className="text-red-600 hover:text-red-900 focus:outline-none focus:underline disabled:opacity-50"
                        >
                          {revoking === token.id ? (
                            <span className="flex items-center">
                              <RefreshCw className="h-4 w-4 mr-1 animate-spin" />
                              Revoking...
                            </span>
                          ) : (
                            <span className="flex items-center">
                              <Trash2 className="h-4 w-4 mr-1" />
                              Revoke
                            </span>
                          )}
                        </button>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
